import React, { useMemo, useState } from 'react';
import { Box, Flex, Text, Button, PresentationalCheckboxGroup } from '@opendoor/bricks/core';

import { FinalizedOffer, UnderContractOffer, ClosedOffer } from 'sellDirect/OfferContext';
import { FileUploadCard } from 'sellDirect/components/FileUploadCard';
import { ImprovedHelloSignTaskFields } from 'sellDirect/helpers/closingTasks';
import {
  FileUpload,
  generateS3Key,
  getS3DocumentDownloadUrl,
  s3DeleteDocument,
  S3UploadDestination,
  uploadFileToS3,
} from 'shared/helpers/s3Helpers';
import { useApolloClient } from '@apollo/client';
import { useUploadDocumentToAdmin } from 'sellDirect/graphql/useUploadDocumentToAdmin';
import { ADMIN_CLIENT_UPLOAD_BUCKET } from 'shared/helpers/globals';
import { capitalizedSnakeCaseToWords } from 'shared/helpers/formatters';
import { OdProtosWebData_Document_Type } from '__generated__/globalTypes';
import { ParticipantsButton } from 'sellDirect/components/participant/ParticipantsButton';

interface AgentDocumentUploadProps {
  offer: FinalizedOffer | UnderContractOffer | ClosedOffer;
  task: ImprovedHelloSignTaskFields;
  onUploaded: () => void;
}

/**
 * Renders the UI for uploading the signed document from an agent
 * @param offer - Finalized offer marked as Agent in Category with underwriting completed
 * @param closingTasks - Current closing tasks
 * @param onUploaded - Callback to call when document is uploaded
 * The fileUploadCard handles the upload of the document to S3, which can be found on AWS
 * Clicking on the submit CTA syncs the document to admin, this can be found in Admin dashboard for this address under documents
 * component-test-check-ignore
 */
export const AgentDocumentUpload: React.FC<AgentDocumentUploadProps> = ({
  offer,
  task,
  onUploaded,
}) => {
  const isPurchaseContract =
    task.documentType ===
    OdProtosWebData_Document_Type.COMBINED_PURCHASE_AGREEMENT_AND_REPAIR_ADDENDUM;
  const [showDeclaration, setShowDeclaration] = useState(false);
  const [hasDeclared, setHasDeclared] = useState<boolean>(!isPurchaseContract);
  const [document, setDocument] = useState<FileUpload>();
  const apolloClient = useApolloClient();
  const { uploadDocumentToAdmin, loading, error } = useUploadDocumentToAdmin();

  const uploadFile = (file: FileUpload, onUpdate: (file: FileUpload) => void) => {
    uploadFileToS3(file, apolloClient, false, onUpdate);
  };

  const getS3Key = (file: File): string => {
    return generateS3Key(file, offer.id, S3UploadDestination.AGENT_CONTRACT);
  };

  const uploadedFiles = useMemo(() => (document ? [document] : []), [document]);

  return (
    <Flex gridGap={5} flexDirection="column">
      <FileUploadCard
        buttonText="Upload signed PDF"
        accept="application/pdf"
        uploadedFiles={uploadedFiles}
        getS3Key={getS3Key}
        uploadFile={(file: FileUpload, onUpdate: (file: FileUpload) => void) => {
          // onUpdate here gets us our progress bar when uploading
          uploadFile(file, onUpdate);
          setShowDeclaration(true);
        }}
        onSingleUploadComplete={(upload: FileUpload) => {
          getS3DocumentDownloadUrl(apolloClient, ADMIN_CLIENT_UPLOAD_BUCKET, upload.s3Key).then(
            (res) => {
              setDocument({
                ...upload,
                downloadUrl: res.data.seller.s3DocumentDownloadUrl ?? undefined,
              });
            },
          );
        }}
        deleteFile={(file: FileUpload) => {
          setDocument(undefined);
          s3DeleteDocument(apolloClient, ADMIN_CLIENT_UPLOAD_BUCKET, file.s3Key);
        }}
        // TODO: Fix copy depends on doc type.
        // https://opendoor.slack.com/archives/C02UT3VBWSY/p1646187234440749
        optionalField={
          <Box mt={[4, null, 0]} ml={[0, null, 7]}>
            <ParticipantsButton
              variant="link"
              text="or invite client to review"
              ariaLabel="Invite clients to review this sale"
            />
          </Box>
        }
        modalDisplayTitle={capitalizedSnakeCaseToWords(task.documentType || '')}
        variant="primary"
      />
      {!isPurchaseContract && (
        <Text>
          Upload signed addendum. Sign and upload the addendum. If you have any questions, just let
          us know.
        </Text>
      )}
      {document && showDeclaration && (
        <>
          {isPurchaseContract && (
            <PresentationalCheckboxGroup
              analyticsName="cake-agent-contract-declaration"
              name="contract-declaration"
              options={[
                {
                  label: (
                    // fix the offset in vertical alignment between text and checkbox.
                    // https://opendoor.slack.com/archives/C02UT3VBWSY/p1646187234440749
                    <Text lineHeight="s1" mt="-4px">
                      By submitting this signed document, I acknowledge that my client is entering
                      into contract to sell their home to Opendoor
                    </Text>
                  ),
                  value: 'hasDeclared',
                },
              ]}
              selectedValues={hasDeclared ? ['hasDeclared'] : []}
              onClick={() => setHasDeclared((prevState) => !prevState)}
              disabled={loading}
            />
          )}
          {/* This submit button syncs the uploaded contract file to admin*/}
          <Button
            mt={3}
            aria-label="Submit document as an agent"
            data-testid="agent-submit-document"
            analyticsName={`cake-agent-submit-document-${task.documentType}`}
            disabled={!hasDeclared || loading}
            onClick={() => {
              uploadDocumentToAdmin(
                onUploaded,
                document.file.name,
                task.id,
                document.s3Key,
                offer.leadToken,
              );
            }}
            loading={loading}
          >
            Submit
          </Button>
          {/* In case of an error we display it here */}
          {error && <Text color="red50">{error.message}</Text>}
        </>
      )}
    </Flex>
  );
};
